import { useState, useEffect, useCallback, useRef } from 'react';
import productsService from '@/services/products.service';
import { Product } from '@/types/product.types';
import { useDebounce } from '@/hooks/useDebounce';

/** Matches the API's default page size. */
export const PRODUCT_PAGE_SIZE = 20;

/** Long enough that typing a word is one request, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 300;

interface UseProductsOptions {
  /** Name search. Debounced here, so pass the raw input value. */
  search?: string;
  /** Exact `productCollection`; omit for all collections. */
  collection?: string;
  /** Remember the loaded list (every page) when the screen unmounts. */
  keepAlive?: boolean;
  /**
   * Start from the remembered list when it matches these filters, instead of
   * refetching page one — returning from a product lands on the same pages,
   * which is what lets the caller restore the scroll position.
   */
  restore?: boolean;
}

interface ProductsSnapshot {
  key: string;
  products: Product[];
  collections: string[];
  total: number;
  seen: Map<string, Product>;
}

let snapshot: ProductsSnapshot | null = null;

export const useProducts = (
  locationId?: string,
  options: UseProductsOptions = {}
) => {
  const { search = '', collection, keepAlive = false, restore = false } = options;
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const requestKey = `${locationId ?? ''}|${debouncedSearch}|${collection ?? ''}`;

  const [restored] = useState(() =>
    restore && snapshot?.key === requestKey ? snapshot : null
  );

  const [products, setProducts] = useState<Product[]>(restored?.products ?? []);
  const [collections, setCollections] = useState<string[]>(
    restored?.collections ?? []
  );
  const [total, setTotal] = useState(restored?.total ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(!restored);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Prices for every product this location has served, filter or no filter.
   *
   * The shop bar totals the cart from the product list, and with search and
   * collection now applied server-side that list no longer contains a product
   * the member added before filtering. Keeping what has been seen means a
   * filtered grid does not price the cart at zero.
   */
  const seenRef = useRef<Map<string, Product>>(restored?.seen ?? new Map());

  /**
   * The request a response must still match to be applied.
   *
   * The shop's club picker can change locationId twice in quick succession,
   * and without this the slower of the two responses wins whichever order they
   * arrive in — leaving the member looking at another venue's stock under their
   * venue's name. The filters are in here for the same reason.
   */
  const requestRef = useRef('');
  /** The request whose first page is what `products` currently holds. */
  const loadedKeyRef = useRef(restored?.key ?? '');

  const fetchPage = useCallback(
    async (offset: number) => {
      const isFirst = offset === 0;
      const key = requestKey;
      isFirst ? setIsLoading(true) : setIsLoadingMore(true);
      if (isFirst) setError(null);

      try {
        const response = await productsService.getProducts({
          locationId,
          limit: PRODUCT_PAGE_SIZE,
          offset,
          search: debouncedSearch,
          collection,
        });
        if (requestRef.current !== key) return;

        response.data.forEach((p) => seenRef.current.set(p.id, p));
        if (isFirst) loadedKeyRef.current = key;
        setProducts((prev) =>
          isFirst ? response.data : [...prev, ...response.data]
        );
        setCollections(response.collections ?? []);
        setTotal(response.total);
      } catch (err: any) {
        if (requestRef.current !== key) return;
        console.error('Error fetching products:', err);
        setError(err.message || 'Failed to fetch products');
        if (isFirst) setProducts([]);
      } finally {
        if (requestRef.current !== key) return;
        if (isFirst) setIsLoading(false);
        else setIsLoadingMore(false);
      }
    },
    [locationId, debouncedSearch, collection, requestKey]
  );

  // Location or filter change starts a new list rather than appending to the
  // previous one's pages. A location change also drops the remembered
  // products — prices and stock are per-venue.
  const seenLocationRef = useRef(locationId);
  useEffect(() => {
    requestRef.current = requestKey;
    // Restored from the snapshot — the list is already this request's.
    if (loadedKeyRef.current === requestKey) return;
    // The location can resolve a render after mount, so the snapshot may
    // only match now rather than at initial state.
    if (restore && snapshot?.key === requestKey) {
      const saved = snapshot;
      seenRef.current = saved.seen;
      seenLocationRef.current = locationId;
      loadedKeyRef.current = requestKey;
      setProducts(saved.products);
      setCollections(saved.collections);
      setTotal(saved.total);
      setError(null);
      setIsLoading(false);
      return;
    }
    if (seenLocationRef.current !== locationId) {
      seenLocationRef.current = locationId;
      seenRef.current.clear();
    }
    fetchPage(0);
  }, [requestKey, fetchPage, locationId, restore]);

  useEffect(() => {
    if (!keepAlive || isLoading || loadedKeyRef.current !== requestKey) return;
    snapshot = {
      key: requestKey,
      products,
      collections,
      total,
      seen: seenRef.current,
    };
  }, [keepAlive, isLoading, requestKey, products, collections, total]);

  const hasMore = products.length < total;

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) return;
    fetchPage(products.length);
  }, [hasMore, isLoading, isLoadingMore, products.length, fetchPage]);

  /** Any product served for this location, whether or not it is in view. */
  const getProduct = useCallback(
    (id: string): Product | undefined => seenRef.current.get(id),
    []
  );

  return {
    products,
    collections,
    total,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    getProduct,
    error,
  };
};
